/**
 * Zone geometry arithmetic for the SuperAdmin SKU setup page (phase 3c).
 *
 * Design handoff: planning/design-handoffs/handoff_2026-08-26/
 *   2026-08-26-zone-geometry/ (ADR 0071 decisions 1 + 2, README section
 *   "Geometry -- the arithmetic to port, not reinvent").
 *
 * WHY THIS IS A SEPARATE MODULE and not methods on the page: this is the
 * arithmetic a wrong `px_per_inch` corrupts silently and forever, so it
 * gets unit tests that do not need a mounted component. The page below it
 * is presentation; this is the part that has to be RIGHT.
 *
 * THE INVARIANT, ported not reinvented
 * ====================================
 *     px_per_inch = line_length_px / measured_inches
 *     w_px = zone.width_in  * px_per_inch
 *     h_px = zone.height_in * px_per_inch
 *     x_px = centre_x_px - w_px / 2
 *     y_px = centre_y_px - h_px / 2
 *
 * ONE width-derived units-per-inch on BOTH axes -- the same rule
 * `placementPreviewGeometry.js:473-486` is emphatic about ("Averaging the
 * two axis scales ... produced neither axis's true scale on a non-square
 * zone -- the mark rendered at the wrong size and floated off its box. Do
 * NOT reintroduce an average"). Because a box here is SIZED from inches it
 * always matches its zone's inch aspect, which is the precondition that
 * makes that single scale correct on both axes.
 *
 * The backend mirror is `api/src/services/zone_dimensions.py`; both are
 * pinned to the handoff's worked example (4.5in at 30.545 px/in ->
 * 137.45px) so the two routes to this number are provably the same one.
 *
 * WHAT IS DELIBERATELY ABSENT: any verdict on whether a scale is RIGHT.
 * ADR 0071 decision 3 refuses one, citing #2791 -- a contrast check that
 * hardcoded `ok: true` in two files and could not fail. Do not add a
 * "scale verified" affordance here or in the page.
 */

// Zone nudging snaps to an eighth of an inch (handoff interaction table).
export const NUDGE_IN = 0.125
export const NUDGE_COARSE_IN = 1.0

/**
 * The scale a caliper drag implies.
 *
 * Returns `null` rather than 0 for a non-positive input. The handoff's
 * `gePpiFrom` returns 0, but a 0 propagated into `zoneBoxPx` yields a
 * zero-size box -- a degenerate box that renders as nothing and says
 * nothing. `null` is a state the caller must handle, which is what makes
 * "no scale" visible in the UI instead of silently drawing an empty stage.
 */
export function pxPerInchFrom (lineLengthPx, measuredInches) {
  if (!(measuredInches > 0)) return null
  if (!(lineLengthPx > 0)) return null
  return lineLengthPx / measuredInches
}

/** Axis-locked line length. ADR 0071 decision 2: the line is axis-locked
 * because "a rotated caliper invites a hypotenuse that reads plausibly and
 * is wrong", so this is a straight delta on the dominant axis, never a
 * hypotenuse. */
export function lineLengthPx (a, b) {
  const dx = Math.abs(b.x - a.x)
  const dy = Math.abs(b.y - a.y)
  return dx >= dy ? dx : dy
}

/*
 * ===================================================================
 * THE SCALE LINE
 * ===================================================================
 *
 * ADR 0071 decision 2 forbids a HYPOTENUSE: "blank shots are square to
 * the camera; a rotated caliper invites a hypotenuse that reads plausibly
 * and is wrong". A diagonal scale line is the worst bug this feature can
 * have -- it renders every decoration on that garment at the wrong size,
 * silently, forever, and decision 3 deliberately refuses the automated
 * check that would catch it.
 *
 * WHAT CHANGED (Jake, 2026-08-27), and why the shape of the guard changed
 * with it. The line could not be moved vertically or re-oriented, so it
 * could only ever measure the dimension it happened to start on. Both are
 * now possible; a diagonal still is not.
 *
 * The OLD guard was a VALIDATION living in the pointer handler: infer the
 * axis by comparing the current |dx| and |dy|, then write only one
 * component of the dragged end. Two things were wrong with that.
 *
 *   1. It made the axis a DERIVED READING of the coordinates rather than
 *      a stated fact about the line, so a vertical line was unreachable
 *      by construction -- a line whose ends share a `y` can only ever be
 *      read back as horizontal, and any flip would be undone by the next
 *      drag. It is also simply wrong on a zero-length line, where
 *      `dx >= dy` picks horizontal for a line that is neither.
 *   2. It was one careless edit from being gone, and nothing downstream
 *      would notice.
 *
 * The guard is now STRUCTURAL. A line is described by four scalars --
 * an orientation plus three positions -- and every mutation below routes
 * through `lineFromSpec`, which writes the shared coordinate to BOTH ends
 * FROM THE SAME VARIABLE. There is no argument to any of these functions
 * that yields `a.y !== b.y` on a horizontal line or `a.x !== b.x` on a
 * vertical one. A diagonal is not rejected; it is unrepresentable.
 *
 * The endpoints stay the stored shape (`{ a, b }`) because that is what
 * the SVG draws and what `lineLengthPx` already consumes; the spec is the
 * constructor, not a second source of truth to keep in sync.
 */

export const LINE_HORIZONTAL = 'horizontal'
export const LINE_VERTICAL = 'vertical'

/**
 * A line's axis.
 *
 * Read from the stored `orientation` when there is one, and only inferred
 * for a legacy line that predates the field. The inference is the old
 * comparison and carries its flaw (a zero-length line reads horizontal),
 * which is exactly why it is the FALLBACK and not the rule.
 */
export function lineOrientation (line) {
  if (!line) return LINE_HORIZONTAL
  if (line.orientation === LINE_VERTICAL) return LINE_VERTICAL
  if (line.orientation === LINE_HORIZONTAL) return LINE_HORIZONTAL
  const dx = Math.abs(line.b.x - line.a.x)
  const dy = Math.abs(line.b.y - line.a.y)
  return dx >= dy ? LINE_HORIZONTAL : LINE_VERTICAL
}

/**
 * Build a line from its axis and three positions. THE ONLY CONSTRUCTOR.
 *
 * `position` is the shared coordinate -- the y of a horizontal line, the
 * x of a vertical one -- and is written to both ends from one variable.
 * `from`/`to` are the two free coordinates along the axis.
 */
export function lineFromSpec ({ orientation, position, from, to }) {
  const axis = orientation === LINE_VERTICAL ? LINE_VERTICAL : LINE_HORIZONTAL
  const shared = Math.round(position)
  const p1 = Math.round(from)
  const p2 = Math.round(to)
  if (axis === LINE_VERTICAL) {
    return {
      orientation: axis,
      a: { x: shared, y: p1 },
      b: { x: shared, y: p2 },
    }
  }
  return {
    orientation: axis,
    a: { x: p1, y: shared },
    b: { x: p2, y: shared },
  }
}

/** A line as its spec -- the inverse of `lineFromSpec`. */
export function lineSpec (line) {
  const axis = lineOrientation(line)
  if (axis === LINE_VERTICAL) {
    return { orientation: axis, position: line.a.x, from: line.a.y, to: line.b.y }
  }
  return { orientation: axis, position: line.a.y, from: line.a.x, to: line.b.x }
}

/**
 * Drag one END along the line's own axis -- this is what SETS the length.
 *
 * The off-axis component of `at` is DISCARDED, not averaged in. That
 * discard is the whole measurement guard, and it is unconditional: the
 * new line is rebuilt from the spec, so the untouched end and the shared
 * coordinate cannot drift no matter where the pointer went.
 */
export function moveLineEnd (line, end, at) {
  const spec = lineSpec(line)
  const along = spec.orientation === LINE_VERTICAL ? at.y : at.x
  return lineFromSpec({
    ...spec,
    from: end === 'a' ? along : spec.from,
    to: end === 'a' ? spec.to : along,
  })
}

/**
 * TRANSLATE the whole ruler -- across the garment AND along its own axis.
 *
 * Jake, 2026-08-27: "I can't move the Ruler around, and can only make it
 * longer or shorter via the handles at each end. I should be able to move
 * it wherever I want it."
 *
 * WHAT WAS ACTUALLY WRONG. A body drag already existed and already worked;
 * it moved the ruler on ONE axis. This function rewrote only `position`
 * (the SHARED coordinate) and discarded the along-axis component, so a
 * horizontal ruler slid up and down and could never travel left or right.
 * The constraint was here in the arithmetic -- not a thin hit target, and
 * not the pointer handler.
 *
 * WHY LIFTING IT IS SAFE, and what still holds. This is a TRANSLATION, so
 * the two properties that make the scale trustworthy are untouched:
 *
 *   * the LENGTH is preserved exactly. `from`/`to` both shift by the same
 *     delta, so `px_per_inch` cannot move while the user is repositioning.
 *     Setting the length stays the endpoint handles' job alone.
 *   * the ruler stays AXIS-LOCKED (ADR 0071 decision 2). A translation
 *     cannot rotate anything, and the result is still built by
 *     `lineFromSpec`, so a hypotenuse remains UNREPRESENTABLE rather than
 *     merely rejected. Free movement and the diagonal guard are orthogonal
 *     -- the guard was never what stopped the ruler from moving.
 *
 * `grab` is where the pointer took hold, and the ruler moves by the
 * pointer's DELTA from it. Anchoring the ruler's centre to the cursor
 * instead would teleport it on grab -- the spot the user grabbed would
 * leap out from under their finger. Callers with no grab point get the
 * original across-only behaviour, because with no delta to compute that is
 * the only honest thing left to do.
 */
export function moveLineAcross (line, at, grab = null) {
  const spec = lineSpec(line)
  const vertical = spec.orientation === LINE_VERTICAL
  const across = vertical ? at.x : at.y
  if (!grab) return lineFromSpec({ ...spec, position: across })
  const alongNow = vertical ? at.y : at.x
  const alongGrab = vertical ? grab.y : grab.x
  const delta = alongNow - alongGrab
  return lineFromSpec({
    ...spec,
    position: across,
    from: spec.from + delta,
    to: spec.to + delta,
  })
}

/**
 * Flip the line between horizontal and vertical, about its own centre.
 *
 * The toggle Jake asked for on 2026-08-27, and the reason a vertical line
 * is now reachable at all. It is a RE-ORIENTATION of one measurement:
 * the length is carried across unchanged, so the inches already typed keep
 * describing the same number of pixels and `px_per_inch` survives the
 * flip. Centring on the old midpoint keeps the line where the user left
 * it rather than teleporting it to a corner.
 */
export function flipLineOrientation (line) {
  const spec = lineSpec(line)
  const flipped = spec.orientation === LINE_VERTICAL ? LINE_HORIZONTAL : LINE_VERTICAL
  const half = Math.abs(spec.to - spec.from) / 2
  const centreAlong = (spec.from + spec.to) / 2
  return lineFromSpec({
    orientation: flipped,
    // The axes trade places: what was the shared coordinate becomes the
    // centre of the new extent, and vice versa.
    position: centreAlong,
    from: spec.position - half,
    to: spec.position + half,
  })
}

/**
 * The pixel rect a zone occupies at a given scale. Extent DERIVED from
 * inches, centre ANCHORED -- so recalibrating keeps a zone where the
 * author put it and only re-derives its size.
 *
 * Returns `null` when there is no usable scale, for the same reason
 * `resolve_zone_geometry` returns `None` rather than a fabricated box: an
 * overlay positioned wrong is worse than no overlay, and this box decides
 * where a logo is printed.
 */
export function zoneBoxPx (zone, pxPerInch) {
  if (!zone) return null
  if (!(pxPerInch > 0)) return null
  if (!(zone.wIn > 0) || !(zone.hIn > 0)) return null
  const w = zone.wIn * pxPerInch
  const h = zone.hIn * pxPerInch
  return { x: zone.cx - w / 2, y: zone.cy - h / 2, w, h }
}

/**
 * Inches implied by dragging a corner to a new pixel extent. The inverse
 * of `zoneBoxPx`, and the reason a resize is recorded as a printable-area
 * override IN INCHES rather than as a pixel rect: pixels are a rendering
 * of the scale, inches are the physical fact.
 */
export function inchesFromPx (extentPx, pxPerInch) {
  if (!(pxPerInch > 0)) return null
  if (!(extentPx > 0)) return null
  return extentPx / pxPerInch
}

/** Snap an inch value to the nudge grid, never below one grid step. */
export function snapInches (value, step = NUDGE_IN) {
  if (!(step > 0)) return value
  const snapped = Math.round(value / step) * step
  return snapped < step ? step : Number(snapped.toFixed(4))
}

/**
 * Resolve a zone's printable area for ONE GARMENT.
 *
 * MIRRORS `resolve_zone_dimensions` (api/src/services/zone_dimensions.py)
 * including its precedence and its safe fallback:
 *   1. this garment's per-(view, zone) override, else
 *   2. the catalog default for the slug, else
 *   3. null -- an unseeded zone is NOT given an invented figure.
 *
 * JAKE'S RULE, 2026-08-26: "the master shouldn't be made into an
 * exception". NOTE THE ABSENT COLOR PARAMETER. Inches are a property of
 * the GARMENT, so this function cannot be passed "the color currently on
 * screen" -- which is exactly how the buggy implementation forks one
 * color out of an edit that looked global. The backend table has no
 * `color_name` column for the same reason; this signature is the frontend
 * half of the same guarantee.
 */
export function resolveZoneDimensions ({ overrides, catalog, viewCode, zoneSlug }) {
  const key = `${viewCode}:${zoneSlug}`
  const override = overrides && overrides[key]
  if (override && override.wIn > 0 && override.hIn > 0) {
    return { wIn: override.wIn, hIn: override.hIn, isOverride: true }
  }
  const cat = catalog && catalog[zoneSlug]
  if (!cat) return null
  return { wIn: cat.wIn, hIn: cat.hIn, isOverride: false }
}

/**
 * REVERT a per-garment dimension override: DELETE the key.
 *
 * Jake's rule: revert must delete, not copy the master's current values
 * in. A copy leaves the zone forked while LOOKING reverted, so a later
 * catalog change never reaches it -- the identical failure one layer down.
 * Returns a NEW object (the page's state is treated as immutable) and
 * omits the key entirely rather than setting it to null, because a null
 * entry would be a second way to say "inherit" that `resolveZoneDimensions`
 * would then have to disambiguate.
 */
export function revertZoneDimensions (overrides, viewCode, zoneSlug) {
  const key = `${viewCode}:${zoneSlug}`
  const next = { ...(overrides || {}) }
  delete next[key]
  return next
}

export default {
  NUDGE_IN,
  NUDGE_COARSE_IN,
  pxPerInchFrom,
  lineLengthPx,
  zoneBoxPx,
  inchesFromPx,
  snapInches,
  resolveZoneDimensions,
  revertZoneDimensions,
}
